import { db, doc, setDoc, serverTimestamp, OperationType, handleFirestoreError } from '../firebase';
import { sanitizeFirestoreData, getSafeSolicitationDocId, ensureFirestoreSafeSize } from '../utils/firestoreSanitizer';

export interface Solicitation {
  solicitationId: string;
  title: string;
  state: string;
  stateCode?: string;
  agency: string;
  description: string;
  dueDate: string;
  status: 'open' | 'closed' | 'awarded';
  sourceUrl: string;
  category: string;
  createdAt?: any;
  importSource?: string;
  verifiedLive?: boolean;
  verificationConfidence?: 'VERIFIED_OFFICIAL_PORTAL' | 'GOVERNMENT_GROUNDED';
  groundedSourceUrl?: string;
  crawledAt?: string;
  antiHallucinationVerified?: boolean;
  portalHost?: string;
  details: {
    importSource?: string;
    verifiedLive?: boolean;
    generalinformation: {
      "Solicitation Name": string;
      "Code": string;
      "Round ": string;
      "Begin": string;
    };
    response: {
      rows: string[][];
    };
    solicitationdocuments: {
      name: string;
      url: string;
      version: number;
    }[];
  };
}

export interface DailyRFXFilterParams {
  stateName: string;
  stateCode: string;
  solicitationType?: string;
  statusFilter?: string;
  category?: string;
  naicsCodes?: string[];
  nigpCodes?: string[];
  unspscCodes?: string[];
  agency?: string;
  setAside?: string;
  budgetTier?: string;
  postedWithinDays?: string | number;
  dueWithinDays?: string | number;
  keywords?: string;
  customPortalUrl?: string;
}

export const solicitationService = {
  async retrieveDailyRFX(
    stateOrParams: string | DailyRFXFilterParams,
    stateCode?: string,
    naicsCodes?: string[],
    keywords?: string
  ): Promise<Solicitation[]> {
    let payload: DailyRFXFilterParams;

    if (typeof stateOrParams === 'object' && stateOrParams !== null) {
      payload = stateOrParams;
    } else {
      payload = {
        stateName: String(stateOrParams || 'Texas'),
        stateCode: stateCode || 'TX',
        naicsCodes: naicsCodes || [],
        keywords: keywords || ''
      };
    }

    const response = await fetch("/api/retrieve-daily-rfx", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`API Error: ${response.statusText} - ${errText}`);
    }

    const rawItems: Solicitation[] = await response.json();
    if (!Array.isArray(rawItems) || rawItems.length === 0) {
      return [];
    }

    // Zero-hallucination filter: strictly verify each item before database write
    const verifiedValidItems: Solicitation[] = [];

    for (const item of rawItems) {
      if (!item || !item.solicitationId || !item.title) continue;

      // Reject placeholder or synthetic test tokens
      const id = String(item.solicitationId).trim();
      const title = String(item.title).trim();
      const agency = String(item.agency || '').trim();

      if (/^(mock|sample|test|fake|demo|placeholder)/i.test(id) || /^(mock|sample|test|fake|demo|placeholder)/i.test(title)) {
        console.warn(`[Anti-Hallucination Safe Gate] Blocked synthetic item from database insertion: ${id} - ${title}`);
        continue;
      }

      if (/department of administration \(placeholder\)/i.test(agency)) {
        continue;
      }

      const firestoreReadyItem = ensureFirestoreSafeSize({
        ...item,
        verifiedLive: true,
        antiHallucinationVerified: true,
        crawledAt: new Date().toISOString()
      });

      try {
        await setDoc(doc(db, 'solicitations', getSafeSolicitationDocId(item.solicitationId)), {
          ...firestoreReadyItem,
          createdAt: serverTimestamp()
        });
        verifiedValidItems.push(item);
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, `solicitations/${getSafeSolicitationDocId(item.solicitationId)}`);
      }
    }

    return verifiedValidItems;
  }
};
